import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils"; // 공통 유틸
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수
import SnGrid from "@shared/components/Grid/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnResizable from "@shared/components/SnResizable";
import SnButton from "@shared/components/SnButton";
import usePanelSizes from "@shared/hooks/usePanelSizes";

interface Props {
  Menuprops: menuInfo;
}

const SN_COS_MAS_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridApiRefH = useRef<any>(null); // 좌측 원가요소 그리드
  const gridApiRefL = useRef<any>(null); // 우측 부문별배부율 그리드
  const searchRef = useRef<any>(null);
  const focusedElemRef = useRef<string>(""); // 좌측에서 선택한 원가요소코드
  const { userInfo } = global();
  //#endregion

  //#region panel 사이즈 관련
  const cd_menu = Menuprops.cd_menu || "SN_COS_MAS_REG_002";
  const defaultSize = { panel1width: "49.5%", panel2width: "49.5%" };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns1, setColums1] = useState<any[]>([]);
  const [columns2, setColums2] = useState<any[]>([]);
  const [dataSource1, setDataSource1] = useState<any[]>([]);
  const [dataSource2, setDataSource2] = useState<any>();
  const [gridFocus, setGridFocus] = useState(false);
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      setDataSource1([]);
      setDataSource2([]);
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColums1(rtn.grid.grid1);
        setColums2(rtn.grid.grid2);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    gridApiRefH.current.selectAndFocusRow(0);
    setGridFocus(false);
  }, [gridFocus]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onSave,
  }));

  //#region 조회조건 헬퍼
  // dt_from/dt_to 는 SnPeriodMonthBoxPop 로 YYYYMMDD 형식이 들어온다. 6자리(YYYYMM)로 변환.
  const toYm = (v: any) => String(v ?? "").slice(0, 6);
  // 지난 기수 = 시작월(YM_START) 의 전월
  const prevYmOf = (dtFrom: any) => {
    const ym = toYm(dtFrom);
    if (ym.length < 6) return "";
    const y = Number(ym.slice(0, 4));
    const m = Number(ym.slice(4, 6));
    const d = new Date(y, m - 2, 1); // m-1(당월,0base) - 1 = 전월
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  //#endregion

  const onSearch = async () => {
    const modifiedRowsL = gridApiRefL.current.getChange();
    if (modifiedRowsL.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        await onSave();
      }
    }
    try {
      const formdata = searchRef.current.getFormdata();
      const params = {
        id: "sn_cos_mas_reg_002_01",
        params: {
          ...formdata,
          no_hst: Number(formdata.no_hst),
        },
      };

      setDataSource1([]);
      setDataSource2([]);
      const data: any = await fnGetDataSet(params);
      setDataSource1(data[0]);
      setDataSource2(data[1] || []);

      if (data[0] && data[0].length > 0) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        setGridFocus(true);
      }

      gridApiRefH.current.resetUpdate();
      gridApiRefL.current.resetUpdate();
    } catch (error: any) {}
  };

  const onSave = async () => {
    const grid2Change = gridApiRefL.current.getChange();

    if (grid2Change.modifiedRowcount === 0) {
      await showDialog("alter", "변경된 내용이 없습니다.");
      return;
    }
    const SaveData = {
      cos_distrate_cc: grid2Change,
    };
    const ok = await fnSave("SN_COS_MAS_REG_002_SAVE_01", SaveData);
    if (!ok) return;
    // 저장 성공 후 변경상태 초기화 — 안 하면 재조회 시 "수정된 내용이 있습니다" 가 뜬다.
    gridApiRefH.current?.resetUpdate();
    gridApiRefL.current?.resetUpdate();
    await onSearch();
  };
  //#endregion

  //#region  Event Method

  // 좌측(원가요소) 행 선택 → 우측을 해당 요소로 필터
  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (!selectedRow) return;
    focusedElemRef.current = selectedRow.cd_elem;
    gridApiRefL.current.applyFilters({ cd_elem: selectedRow.cd_elem });
    gridApiRefL.current.selectAndFocusRow(0);
  };

  // 현재 선택된 원가요소 행(좌측) 반환
  const getFocusedElemRow = () => {
    const row = gridApiRefH.current?.getSelectedRow?.();
    if (row && row.cd_elem) return row;
    // 폴백: 필터 기준으로 보관해둔 코드로 원본에서 찾는다
    return dataSource1?.find(
      (r: any) => r.cd_elem === focusedElemRef.current
    );
  };

  /**
   * 전개 : 선택한 원가요소에 대해 부문(MAS_CC)을 우측에 "전부 재생성(배부율 0)".
   *  - 1차수(no_hst=1) : fg_cc in ('1','2')  제조·보조부문
   *  - 2차수(no_hst=2) : fg_cc = '1'         제조부문
   *  기존 해당 요소 행은 삭제(delete) 후 신규(insert)로 다시 만든다.
   */
  const onExpand = async () => {
    const formdata = searchRef.current.getFormdata();
    const elemRow = getFocusedElemRow();
    if (!elemRow) {
      await showDialog("alter", "원가요소를 먼저 선택해 주세요.");
      return;
    }

    const cd_firm = userInfo.cd_firm;
    const cd_biz = formdata.cd_biz;
    const no_hst = Number(formdata.no_hst);
    const fg_cost = formdata.fg_cost;
    const ym_end = toYm(formdata.dt_to);
    const cd_elem = elemRow.cd_elem;

    // 1차(no_hst=1)는 공통비+부문비, 2차는 공통비만. 분기는 쿼리 안에서 :no_hst 로 한다.
    const ccRtn: any = await fnGetDataSet({
      id: "sn_cos_mas_reg_002_05",
      params: { cd_firm, cd_biz, no_hst },
    });
    const ccList: any[] = Array.isArray(ccRtn?.[0]) ? ccRtn[0] : [];
    if (ccList.length === 0) {
      await showDialog("alter", "전개할 부문이 없습니다.");
      return;
    }

    // 해당 원가요소의 기존 우측 행 제거(필터가 이미 해당 요소로 걸려 있음)
    gridApiRefL.current.selectAllFiltered();
    gridApiRefL.current.deleteSelectedRows();

    // 부문별 신규 행(배부율 0) 추가
    for (const cc of ccList) {
      gridApiRefL.current.addRow({
        cd_firm,
        cd_biz,
        ym_end,
        no_hst,
        cd_elem,
        fg_cost,
        cd_cc: cc.cd_cc,
        nm_cc: cc.nm_cc,
        fg_cc: cc.fg_cc,
        nm_fg_cc: cc.nm_fg_cc,
        yn_use: cc.yn_use,
        qt_rate: 0,
      });
    }
    gridApiRefL.current.applyFilters({ cd_elem });
    gridApiRefL.current.selectAndFocusRow(0);
    await showDialog("alter", "전개가 완료되었습니다.");
  };

  /**
   * 이전자료참조 : 지난 기수(YM_START-1월)의 배부율을 우측에 뿌린다(미저장).
   *  현재 기수(ym_end)로 신규(insert) 행을 만들되 배부율은 전기 값으로 채운다.
   *  사용자가 확인/수정 후 저장 버튼으로 현재 기수에 저장한다.
   */
  const onCopyPrev = async () => {
    const formdata = searchRef.current.getFormdata();
    const elemRow = getFocusedElemRow();
    if (!elemRow) {
      await showDialog("alter", "원가요소를 먼저 선택해 주세요.");
      return;
    }

    const cd_firm = userInfo.cd_firm;
    const cd_biz = formdata.cd_biz;
    const no_hst = Number(formdata.no_hst);
    const fg_cost = formdata.fg_cost;
    const ym_end = toYm(formdata.dt_to);
    const prev_ym = prevYmOf(formdata.dt_from);
    const cd_elem = elemRow.cd_elem;

    if (!prev_ym) {
      await showDialog("alter", "원가기간을 확인해 주세요.");
      return;
    }

    const prevRtn: any = await fnGetDataSet({
      id: "sn_cos_mas_reg_002_06",
      params: { cd_firm, cd_biz, no_hst, cd_elem, prev_ym },
    });
    const prevList: any[] = Array.isArray(prevRtn?.[0]) ? prevRtn[0] : [];
    if (prevList.length === 0) {
      await showDialog("alter", `지난 기수(${prev_ym}) 배부율 데이터가 없습니다.`);
      return;
    }

    // 현재 요소의 기존 우측 행 제거 후, 전기 배부율로 신규 행 생성(미저장)
    gridApiRefL.current.selectAllFiltered();
    gridApiRefL.current.deleteSelectedRows();

    for (const cc of prevList) {
      gridApiRefL.current.addRow({
        cd_firm,
        cd_biz,
        ym_end,
        no_hst,
        cd_elem,
        fg_cost,
        cd_cc: cc.cd_cc,
        nm_cc: cc.nm_cc,
        fg_cc: cc.fg_cc,
        nm_fg_cc: cc.nm_fg_cc,
        yn_use: cc.yn_use,
        qt_rate: cc.qt_rate ?? 0,
      });
    }
    gridApiRefL.current.applyFilters({ cd_elem });
    gridApiRefL.current.selectAndFocusRow(0);
    await showDialog("alter", "이전자료참조가 완료되었습니다.");
  };
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex space-x-1.5 h-full w-full">
        <SnResizable
          direction={"width"}
          seq={1}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel1width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <SnGrid
            seq={1}
            ref={gridApiRefH}
            columnConfig={columns1}
            rowData={dataSource1}
            isEditable={false}
            rowSelection="single"
            title="원가요소"
            onFocusedRowChanged={onFocusedRowChanged}
            cellSelection={false}
          />
        </SnResizable>
        <SnResizable
          direction={"width"}
          seq={2}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel2width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <SnGrid
            seq={2}
            ref={gridApiRefL}
            columnConfig={columns2}
            rowData={dataSource2}
            enableExternalChart={false}
            isEditable={true}
            rowSelection="multiple"
            title="부문별배부율"
            cellSelection={false}
            header={
              <div className="relative flex space-x-1">
                <SnButton text="전개" onClick={onExpand} />
                <SnButton text="이전자료참조" onClick={onCopyPrev} />
              </div>
            }
          />
        </SnResizable>
      </div>
    </div>
  );
  //#endregion
});

export default SN_COS_MAS_REG_002;
